import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, Popconfirm, Switch, Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import SelectLn2Message from "../../../../components/select/SelectLn2Message";
import EditableRowCard from "./EditableRowCard";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import DataValueEditor from "./DataValueEditor";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
// INTERFACES
import type {
  OpcUaDataValueForm,
  OpcUaMethodMappingForm,
  OpcUaNodeIdForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;

const BLANK_ARGUMENT: OpcUaDataValueForm = { kind: "string", value: "" };

/** True when a NodeId form has a non-empty identifier (or, for a BrowsePath, at least one hop). */
function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

/**
 * Ordered input-arguments list for one method call. Every argument can be
 * edited (its value is loaded into the staging form below, which then reads
 * "Update Argument"), removed, or a new one staged and appended.
 */
function InputArgumentsEditor({
  args,
  onChangeArgs,
}: {
  args: OpcUaDataValueForm[];
  onChangeArgs: (args: OpcUaDataValueForm[]) => void;
}) {
  const [argDraft, setArgDraft] = useState<OpcUaDataValueForm>({ ...BLANK_ARGUMENT });
  // null = staging a new argument; an index = rewriting that stored argument.
  const [editingArg, setEditingArg] = useState<number | null>(null);

  const onSubmitArgument = () => {
    if (editingArg !== null) {
      const updated = [...args];
      updated[editingArg] = argDraft;
      onChangeArgs(updated);
      setEditingArg(null);
    } else {
      onChangeArgs([...args, argDraft]);
    }
    setArgDraft({ ...BLANK_ARGUMENT });
  };
  const onEditArgument = (index: number) => {
    setArgDraft(args[index]);
    setEditingArg(index);
  };
  const onCancelEditArgument = () => {
    setArgDraft({ ...BLANK_ARGUMENT });
    setEditingArg(null);
  };
  const onRemove = (index: number) => {
    onChangeArgs(args.filter((_, i) => i !== index));
    if (editingArg === index) onCancelEditArgument();
  };

  return (
    <div className="mt-2">
      <span className="generalStyles__inputLabel">Input Arguments ({args.length})</span>
      {args.map((arg, index) => (
        <div
          key={index}
          className="mb-2 generalStyles__flex"
          style={{
            backgroundColor: "#161616",
            borderRadius: "5px",
            padding: "8px",
            outline: editingArg === index ? "1px solid #1677ff" : undefined,
          }}
        >
          <div style={{ width: "100%" }}>
            <div data-testid="row-summary" className="generalStyles__textEllipsis">
              [{index}] {describeArgument(arg)}
            </div>
          </div>
          <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
            <Tooltip title="Edit argument">
              <Button
                aria-label="Edit argument"
                onClick={() => onEditArgument(index)}
                className="buttonStyle__29"
                shape="circle"
                icon={<FontAwesomeIcon icon={faPen} />}
              />
            </Tooltip>
          </div>
          <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
            <Popconfirm
              title="Delete this argument? This cannot be undone."
              onConfirm={() => onRemove(index)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Remove argument">
                <Button
                  aria-label="Remove argument"
                  className="buttonStyle__29"
                  shape="circle"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        </div>
      ))}
      <div className="row">
        <DataValueEditor value={argDraft} onChange={setArgDraft} label="Argument" />
      </div>
      <div className="row mt-2">
        <div className={editingArg === null ? "col-12" : "col-9"}>
          <Button onClick={onSubmitArgument} className="generalStyles__width100 buttonStyle__28">
            <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
            {editingArg === null ? "Add Argument" : "Update Argument"}
          </Button>
        </div>
        {editingArg !== null && (
          <div className="col-3">
            <Button onClick={onCancelEditArgument} danger className="generalStyles__width100">
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** One argument as `kind = value`, an array as its element count. */
function describeArgument(arg: OpcUaDataValueForm): string {
  if (arg.kind === "array") return `array (${(arg.elements ?? []).length} element(s))`;
  return `${arg.kind} = ${arg.value}`;
}

/** The fields of one method mapping: LN2 message, active flag, object and method nodes, ordered input arguments. Shared by the create form and a card's in-place edit. */
function MethodForm({
  method,
  onChangeMethod,
  disabled = false,
}: {
  method: OpcUaMethodMappingForm;
  onChangeMethod: (method: OpcUaMethodMappingForm) => void;
  disabled?: boolean;
}) {
  return (
    // While the create form is parked behind an in-place edit, the nested
    // editors (which have no disabled prop of their own) are frozen too.
    <div
      aria-disabled={disabled || undefined}
      style={disabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
    >
      <div className="row">
        <div className="col-12 col-md-8 mt-2">
          <span className="generalStyles__inputLabel">LN2 Message</span>
          <SelectLn2Message
            value={method.msgId}
            onChange={(key) => onChangeMethod({ ...method, msgId: key })}
          />
        </div>
        <div className="col-12 col-md-4 mt-2" style={{ textAlign: "center" }}>
          <OpcUaFieldLabel label="Active" />
          <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
            <Switch
              checked={method.active}
              onChange={(checked) => onChangeMethod({ ...method, active: checked })}
            />
          </div>
        </div>
      </div>
      <div className="mt-2">
        <span className="generalStyles__inputLabel">Object Node</span>
        <NodeIdEditor
          node={method.objectId}
          onChangeNode={(objectId) => onChangeMethod({ ...method, objectId })}
          disabled={disabled}
        />
      </div>
      <div className="mt-3">
        <span className="generalStyles__inputLabel">Method Node</span>
        <NodeIdEditor
          node={method.methodId}
          onChangeNode={(methodId) => onChangeMethod({ ...method, methodId })}
          disabled={disabled}
        />
      </div>
      <InputArgumentsEditor
        args={method.inputArguments}
        onChangeArgs={(inputArguments) => onChangeMethod({ ...method, inputArguments })}
      />
    </div>
  );
}

const blankMethod = (): OpcUaMethodMappingForm => ({
  msgId: "",
  active: true,
  objectId: { ...BLANK_NUMERIC_NODE_ID },
  methodId: { ...BLANK_NUMERIC_NODE_ID },
  inputArguments: [],
});

/** LN2 command -> OPC UA method-call mappings: object node, method node, and ordered input arguments. Rows are created, edited in place, and deleted. */
export default function MethodsEditor({
  methods,
  onChangeMethods,
}: {
  methods: OpcUaMethodMappingForm[];
  onChangeMethods: (rows: OpcUaMethodMappingForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draft, setDraft] = useState<OpcUaMethodMappingForm>(blankMethod);
  // null = authoring a new row with the create form; an index = editing that
  // stored row in place (its card grows the form, the create form is disabled).
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const canAdd =
    draft.msgId !== "" && isNodeComplete(draft.objectId) && isNodeComplete(draft.methodId);
  const onSubmit = () => {
    if (!canAdd) return;
    if (editingIndex !== null) {
      const updated = [...methods];
      updated[editingIndex] = draft;
      onChangeMethods(updated);
      setEditingIndex(null);
    } else {
      onChangeMethods([...methods, draft]);
    }
    setDraft(blankMethod());
  };
  const onEdit = (index: number) => {
    setDraft(methods[index]);
    setEditingIndex(index);
  };
  const onCancelEdit = () => {
    setDraft(blankMethod());
    setEditingIndex(null);
  };
  const onRemove = (index: number) => {
    onChangeMethods(methods.filter((_, i) => i !== index));
    if (editingIndex === index) onCancelEdit();
  };

  const isEditing = editingIndex !== null;

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <Collapse defaultActiveKey={["methods"]}>
        <Panel header={t("general.opcUaMethodMappings", { total: methods.length })} key="methods">
          {methods.map((stored, index) => {
            const editing = editingIndex === index;
            // While editing, the summary follows the draft so it reacts to every change.
            const method = editing ? draft : stored;
            return (
              <EditableRowCard
                key={`${stored.msgId}-${index}`}
                borderColor="rgba(255, 255, 255, 0.15)"
                tags={
                  <Tag style={{ textAlign: "center" }}>{method.active ? "Active" : "Inactive"}</Tag>
                }
                raw={`${method.msgId} (${method.active ? "active" : "inactive"}) → object=${describeNode(method.objectId)}; method=${describeNode(method.methodId)} (${method.inputArguments.length} arg(s))`}
                editing={editing}
                dimmed={isEditing && !editing}
                canSave={canAdd}
                deleteTitle="Delete this method mapping? This cannot be undone."
                onEdit={() => onEdit(index)}
                onSave={onSubmit}
                onCancel={onCancelEdit}
                onRemove={() => onRemove(index)}
              >
                <MethodForm method={draft} onChangeMethod={setDraft} />
              </EditableRowCard>
            );
          })}
          <MethodForm method={draft} onChangeMethod={setDraft} disabled={isEditing} />
          <div className="row mt-2">
            <div className="col-12">
              <Button
                onClick={onSubmit}
                disabled={!canAdd || isEditing}
                className={`generalStyles__width100 ${canAdd && !isEditing ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add Method Mapping
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
